import "./style.css";

import { HelpMessage } from "@ids/react-help-message";
import {
  RadioButton,
  RadioButtonInput,
  type RadioButtonInputProps,
} from "@ids/react-radio-button";
import classNames from "classnames";
import type { Ref } from "react";
import type { InputNecessity } from "@/utils/types";
import { ErrorMessage } from "../errorMessage/ErrorMessage";
import { Popover } from "../popover/Popover";

interface ComposedRadioButtonProps
  extends Omit<RadioButtonInputProps, "popover"> {
  children?: React.ReactNode;
  necessity?: InputNecessity;
  value?: string | number;
  style?: React.CSSProperties;
  label?: string | React.ReactNode;
  popoverHeader?: string | React.ReactNode;
  popoverMessage?: string | React.ReactNode;
  helpMessage?: string;
  errorMessage?: string | false;
}

interface ComposedRadioButtonValueProps {
  defaultChecked?: boolean;
  disabled?: boolean;
  name: string;
  id: string;
  label: string | undefined | null;
  value: string | number;
}

export const ComposedRadioButtonValue = ({
  defaultChecked,
  disabled,
  id,
  value,
  label,
  name,
  ...props
}: ComposedRadioButtonValueProps) => {
  return (
    <div className="if flex-row gap-4 align-items-center">
      <RadioButtonInput
        disabled={disabled}
        className="if radio-button-input"
        defaultChecked={defaultChecked}
        id={id}
        name={name}
        value={value}
        type="radio"
        {...props}
      />
      {label && (
        <label className="if label" htmlFor={id}>
          {label}
        </label>
      )}
    </div>
  );
};

export const ComposedRadioButton = (
  {
    id,
    label,
    children,
    necessity,
    disabled,
    helpMessage,
    errorMessage,
    className,
    popoverHeader,
    popoverMessage,
    ...props
  }: ComposedRadioButtonProps,
  ref?: Ref<HTMLInputElement>,
) => {
  const necessityClass = necessity ?? "";
  const disabledClass = disabled ? "is-disabled" : "";
  const errorMessageId = `${id}-error-message`;
  const helpMessageId = `${id}-help-message`;
  const invalid = !!errorMessage;
  const ariaDescribedby = invalid ? errorMessageId : helpMessageId;

  const classes = classNames(className, "if flex-column gap-16");

  return (
    <div className={classes}>
      <label
        className={`if label flex-row align-items-center justify-content-space-between ${necessityClass} ${disabledClass}`}
        htmlFor={id}
      >
        <span>{label}</span>
        <Popover
          popoverHeader={popoverHeader}
          popoverMessage={popoverMessage}
        />
      </label>
      <RadioButton
        aria-disabled={disabled}
        id={id}
        invalid={invalid ? true : undefined}
        required={necessity === "is-required"}
        aria-describedby={ariaDescribedby}
        className="flex gap-24 flex-wrap radio-button"
        {...props}
        ref={ref}
      >
        {children}
      </RadioButton>
      {!invalid && helpMessage && (
        <HelpMessage id={helpMessageId} disabled={disabled}>
          {helpMessage}
        </HelpMessage>
      )}
      {invalid && errorMessage && (
        <ErrorMessage id={errorMessageId}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};
